import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { ariaDescribedByIds, type WidgetProps } from '@rjsf/utils'
import styles from './NumberWidget.module.scss'

const INTEGER_PATTERN = /^-?\d*$/
const DECIMAL_PATTERN = /^-?\d*\.?\d*$/

const toDecimalText = (value: number): string => value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })

const formatNumber = (value: unknown): string =>
  typeof value === 'number' && Number.isFinite(value) ? toDecimalText(value) : typeof value === 'string' ? value : ''

const valueOf = (text: string, emptyValue?: unknown): unknown => {
  if (text === '') {
    return emptyValue
  }
  const parsed = Number(text)
  return Number.isFinite(parsed) && !['-', '.', '-.'].includes(text) ? parsed : text
}

const rangeCaption = (minimum: unknown, maximum: unknown): string | null => {
  const hasMin = typeof minimum === 'number'
  const hasMax = typeof maximum === 'number'
  if (hasMin && hasMax) {
    return `${minimum}–${maximum}`
  }
  if (hasMin) {
    return `Min ${minimum}`
  }
  if (hasMax) {
    return `Max ${maximum}`
  }
  return null
}

export const NumberWidget: FC<WidgetProps> = ({
  id,
  value,
  schema,
  options,
  placeholder,
  required,
  disabled,
  readonly,
  rawErrors,
  onChange,
  onBlur,
  onFocus,
}) => {
  const integer = schema.type === 'integer'
  const [text, setText] = useState(formatNumber(value))
  const [syncedValue, setSyncedValue] = useState<unknown>(value)
  const caption = rangeCaption(schema.minimum, schema.maximum)
  const captionId = `${id}__range`

  if (value !== syncedValue) {
    setSyncedValue(value)
    if (valueOf(text, options.emptyValue) !== value) {
      setText(formatNumber(value))
    }
  }

  const handleChange = (raw: string) => {
    const next = raw.replace(',', '.')
    if (!(integer ? INTEGER_PATTERN : DECIMAL_PATTERN).test(next)) {
      return
    }
    setText(next)
    onChange(valueOf(next, options.emptyValue))
  }

  return (
    <div className='d-flex align-items-center zocks-gap-0.75'>
      <Form.Control
        id={id}
        type='text'
        inputMode={integer ? 'numeric' : 'decimal'}
        className={styles.input}
        value={text}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        isInvalid={Boolean(rawErrors?.length)}
        aria-describedby={caption ? `${ariaDescribedByIds(id)} ${captionId}` : ariaDescribedByIds(id)}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={() => onBlur(id, valueOf(text, options.emptyValue))}
        onFocus={() => onFocus(id, valueOf(text, options.emptyValue))}
      />
      {caption ? (
        <span id={captionId} className='paragraph-small zocks-text-gray-600'>
          {caption}
        </span>
      ) : null}
    </div>
  )
}
